import CloseIcon from '@mui/icons-material/Close';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Box, Button, IconButton, SxProps, useTheme } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { FC, PropsWithChildren, useEffect, useState } from 'react';

const drawerPaperSx: SxProps = {
    backgroundColor: 'grey.700',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    ml: -3,
    mr: 3,
    pt: 3,
    transition: 'left 0.5s ease-in-out',
    width: '34%',
};

const expandButtonSx: SxProps = {
    '& span': {
        m: 0,
    },
    alignItems: 'center',
    backgroundColor: 'common.white',
    border: '1px solid',
    borderColor: 'primary.light',
    borderRadius: 1,
    color: 'primary.main',
    display: { md: 'none', xs: 'flex' },
    fontWeight: 'medium',
    gap: 1,
    justifyContent: 'center',
    minWidth: 'inherit',
    my: 3,
    px: 1,
    py: 0.5,
};

const backdropSx: SxProps = {
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent
    height: '100%',
    left: 0,
    opacity: 0,
    pointerEvents: 'none', // Allow clicks to pass through
    position: 'fixed',
    top: 0,
    transition: 'opacity 0.5s ease, pointer-events 0.5s ease', // Transition for opacity
    width: '100%',
    zIndex: 1200, // Lower than the overlay box but higher than content
};

const SideBar: FC<PropsWithChildren> = ({ children }) => {
    const { t } = useTranslation('books');
    const [open, setOpen] = useState(true);
    const [isDesktop, setIsDesktop] = useState(true);
    const theme = useTheme();
    const mobileBreakpoint = theme.breakpoints.values.md;

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    useEffect(() => {
        if (window !== undefined) {
            setIsDesktop(window.innerWidth > mobileBreakpoint);
        }
    }, [mobileBreakpoint]);

    return (
        <>
            {open ? (
                <Box
                    onClick={handleClose}
                    sx={{
                        ...backdropSx,
                        opacity: open && !isDesktop ? 1 : 0,
                        pointerEvents: open && !isDesktop ? 'auto' : 'none',
                    }}
                />
            ) : null}
            <Button
                aria-label="open-book-index"
                onClick={handleOpen}
                startIcon={<FormatListBulletedIcon />}
                sx={expandButtonSx}
                title={t('summary')}
            >
                {t('summary')}
            </Button>
            <Box
                sx={{
                    ...drawerPaperSx,
                    left: open ? 0 : '-100%',
                    ml: !isDesktop ? 0 : -3,
                    position: isDesktop ? 'relative' : 'fixed',
                    top: 0,
                    width: isDesktop ? '34%' : '80%',
                    zIndex: 1300,
                }}
            >
                {!isDesktop && (
                    <IconButton
                        onClick={handleClose}
                        size="large"
                        sx={{
                            height: 3,
                            position: 'absolute',
                            right: 24,
                            width: 3,
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon color="secondary" />
                    </IconButton>
                )}
                {children}
            </Box>
        </>
    );
};

export default SideBar;
